<blaze-dialog contentClass="grow" [showFooter]="status() === DialogStatus.Form">
  <div icon>
    <mat-icon svgIcon="icon_ai_24" class="block w-24 h-24 icon-24" />
  </div>
  <div title>Create a Theme with AI</div>

  <div content class="grow" data-cy="CreateThemeDialog:content">
    @switch (status()) {
      @case (DialogStatus.Form) {
        <ng-container [ngTemplateOutlet]="formTpl" />
      }

      @case (DialogStatus.CreatingAI) {
        <ng-container [ngTemplateOutlet]="generatingThemeTpl" />
      }
    }
  </div>

  @if (status() === DialogStatus.Form) {
    <div footer class="footer-actions">
      @if (error()) {
        <span class="text-warn text-13">{{ error() }}</span>
      }
      <button
        data-cy="CreateThemeDialog:ai-theme-button"
        class="action-button"
        blazeButton
        blazeButtonSize="xl"
        type="button"
        blazeTooltip="Results may be incorrect or incomplete. Please review before applying."
        matTooltipPosition="above"
        (click)="submitCreateTheme()"
        [disabled]="!canCreateTheme()"
      >
        Create theme with AI
        <span
          class="rounded bg-base-light text-accent uppercase inline-flex items-center justify-center px-3 text-11 font-medium"
          >Beta</span
        >
      </button>
    </div>
  }
</blaze-dialog>

<ng-template #formTpl>
  <form class="form" (ngSubmit)="submitCreateTheme()" [formGroup]="form">
    <div class="form-control mb-24">
      <blaze-inplace-edit
        #themeNameField
        class="theme-name-field"
        data-cy="CreateThemeDialog:themeNameField"
        [value]="nameValue"
        [commitOnBlur]="true"
        [isEditing]="isEditingName"
        (committed)="commitNameValue($event)"
        (submitRequested)="submitCreateTheme()"
        (draftChanged)="draftNameValue($event)"
        (cancelled)="cancelNameEditing()"
      >
        <div
          inplace-display
          class="inplace-display"
          [tabindex]="0"
          (keydown.space)="editNameValue(themeNameField)"
          (keydown.enter)="editNameValue(themeNameField)"
          (click)="editNameValue(themeNameField)"
        >
          <div class="value" [blazeTooltip]="nameValue">{{ nameValue }}</div>
          <div class="actions">
            <button blazeIconButton class="action" (click)="editNameValue(themeNameField)">
              <mat-icon svgIcon="icon_edit_24" />
            </button>
          </div>
        </div>
      </blaze-inplace-edit>

      @if (isNameInUse()) {
        <div class="mt-8 text-warn" data-cy="CreateThemeDialog:duplicateName">
          This theme name is already in use. Please choose a different one.
        </div>
      }
    </div>

    <div class="form-control">
      <div class="flex flex-col gap-4 bg-base-light p-16">
        <h3>Reference Images</h3>
        <p class="text-primary-darker pb-8">
          Attach one or more screenshots to generate a theme from.
        </p>

        <div class="relative">
          <textarea
            id="create-theme-idea-textarea"
            class="bd-textarea w-full theme-idea-field"
            formControlName="idea"
            placeholder="Optional - add anything that helps us match your design more precisely. Especially useful: typography (font family, text and heading sizes, line heights) and real dimensions (page width, or the size of a key element like a button or card). Exact sizes give the generator an anchor to scale everything correctly."
          >
          </textarea>
          <button
            type="button"
            blazeIconButton
            class="absolute bottom-8 right-8"
            blazeTooltip="Attach reference images"
            (click)="uploadAttachments()"
            data-cy="CreateThemeDialog:attachFiles"
          >
            <mat-icon svgIcon="attach" class="icon-24" />
          </button>
        </div>

        @if (attachments().length) {
          <div class="flex flex-wrap gap-8">
            @for (attachment of attachments(); track attachment.url) {
              <div
                class="flex items-center gap-4 px-8 py-2 rounded-2 text-primary-darker border border-overlay-light"
              >
                <mat-icon svgIcon="document_attach" class="icon-16" />
                <span class="whitespace-nowrap">{{ attachment.filename }}</span>
                <button
                  type="button"
                  blazeIconButton
                  blazeIconButtonSize="sm"
                  blazeTooltip="Remove attachment"
                  (click)="removeAttachment(attachment)"
                >
                  <mat-icon svgIcon="icon_close_8" />
                </button>
              </div>
            }
          </div>
        }

        @if (showAdvancedAiOptions) {
          <div class="flex flex-col gap-4 pt-8">
            <label for="create-theme-model-select">Model</label>
            <blaze-select
              id="create-theme-model-select"
              [allowNone]="true"
              [multiselect]="false"
              [options]="modelOptions"
              [showSelection]="true"
              formControlName="model"
              data-cy="CreateThemeDialog:modelSelect"
            />
          </div>
        }
      </div>
    </div>

    <input #submitInput type="submit" class="submit-input" />
  </form>
</ng-template>

<ng-template #generatingThemeTpl>
  <div class="flex flex-col items-center justify-center grow gap-40 py-72">
    <div class="flex flex-col gap-8 items-center">
      <div class="text-base-white bg-accent p-16 rounded">
        <mat-icon svgIcon="icon_ai_24" class="icon-24" />
      </div>
      <h1>{{ nameValue }}</h1>
    </div>

    <div class="flex flex-col gap-8">
      <blaze-element-loading-indicator [isVisible]="true" class="!static" />
      <p class="dialog-message"> AI is generating your theme. This may take a few moments. </p>
    </div>
  </div>
</ng-template>
